import type { ReactNode } from "react";
import { Box, ChevronRight, ExternalLink } from "lucide-react";
import type { TLRecord } from "@tldraw/tlschema";
import type {
  AgentResource,
  AgentToolCall,
  DraftChange,
} from "../shared/agent-drafts";
import { recordLabel } from "../shared/agent-drafts";
import { plainToolError } from "../shared/agent-errors";
import type { AgentRunInfo } from "../shared/agent-runtime";
import {
  elementKind,
  elementOutline,
  sceneExtent,
  type SceneSpec,
} from "../shared/scene-spec";

/** Render plain agent text with safe http(s) links. */
export function AgentText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let at = 0;
  for (const match of text.matchAll(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>]+)/g,
  )) {
    const start = match.index!;
    parts.push(text.slice(at, start));
    const rawUrl = match[2] ?? match[3];
    const url = match[2] ?? rawUrl.replace(/[.,;:!?]+$/, "");
    try {
      const parsed = new URL(url);
      if (parsed.username || parsed.password) throw new Error();
      parts.push(
        <a key={start} href={url} target="_blank" rel="noreferrer noopener">
          {match[1] ?? url}
        </a>,
      );
    } catch {
      parts.push(match[0]);
    }
    if (match[3] && rawUrl.length > url.length)
      parts.push(rawUrl.slice(url.length));
    at = start + match[0].length;
  }
  parts.push(text.slice(at));
  return <>{parts}</>;
}

const toolLabels: Record<string, string> = {
  web_search: "Searched the web",
  search_web: "Searched the web",
  browse_page: "Read a web page",
  search_images: "Looked for reference images",
  gather_image: "Collected a reference image",
  read_image: "Inspected an image",
  generate_image: "Generated an image",
  add_image: "Placed an image",
  edit_canvas: "Edited the canvas",
  read_canvas: "Read the canvas",
  measure_canvas: "Measured the drawing",
  workspace_tool: "Used a workspace tool",
};
const runningLabels: Record<string, string> = {
  web_search: "Searching the web",
  search_web: "Searching the web",
  browse_page: "Reading a web page",
  search_images: "Looking for reference images",
  gather_image: "Collecting a reference image",
  read_image: "Inspecting an image",
  generate_image: "Generating an image",
  add_image: "Placing an image",
  edit_canvas: "Editing the canvas",
  read_canvas: "Reading the canvas",
  measure_canvas: "Measuring the drawing",
  workspace_tool: "Using a workspace tool",
};
export function toolLabel(name: string, running = false) {
  return (running ? runningLabels : toolLabels)[name] ?? name;
}
/** What a step did, naming the sheet it read or the objects it looked at. */
export function callLabel(
  call: AgentToolCall,
  nameOf?: (id: string) => string | undefined,
  running = false,
) {
  const input = (call.input ?? {}) as {
    sheet?: string;
    query?: string;
    request?: { action?: string; ids?: string[] };
  };
  const name = (id: string) => nameOf?.(id) ?? id;
  if (call.name === "read_canvas" && input.sheet) {
    const sheet = name(input.sheet);
    // "Read Sheet 2", but "Read sheet A-101".
    return `${running ? "Reading" : "Read"} ${/^sheet\b/i.test(sheet) ? "" : "sheet "}${sheet}`;
  }
  if (call.name === "read_canvas" && input.query)
    return `${running ? "Searching" : "Searched"} the board for “${input.query}”`;
  const ids = input.request?.ids ?? [];
  if (
    call.name === "workspace_tool" &&
    input.request?.action === "render" &&
    ids.length
  )
    return `${running ? "Looking at" : "Looked at"} ${
      ids.length <= 2
        ? ids.map(name).join(" and ")
        : `${name(ids[0])} and ${ids.length - 1} more`
    }`;
  return toolLabel(call.name, running);
}
function callDetail(call: AgentToolCall) {
  const input = call.input as Record<string, unknown> | null;
  const text =
    input?.title ??
    input?.prompt ??
    input?.query ??
    (Array.isArray(input?.queries) ? input.queries.join(", ") : undefined) ??
    input?.url ??
    input?.name ??
    "";
  return String(text).slice(0, 160);
}
export function modelLabel(run: AgentRunInfo) {
  const m = run.model;
  const gemini = /^gemini-([\d.]+)-(flash|pro)(-lite)?/i.exec(m);
  if (gemini)
    return `Gemini ${gemini[1]} ${gemini[2] === "pro" ? "Pro" : "Flash"}${gemini[3] ? " Lite" : ""}`;
  const claude = /^claude-(\w+)-(\d)-(\d)/i.exec(m);
  if (claude)
    return `Claude ${claude[1][0].toUpperCase()}${claude[1].slice(1)} ${claude[2]}.${claude[3]}`;
  return m;
}
function tokens(n: number) {
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}k` : `${n}`;
}

/** Join step labels into one sentence: "Read the canvas and edited the canvas". */
function stepSentence(labels: string[]) {
  const words = labels.map((l, i) => (i ? l[0].toLowerCase() + l.slice(1) : l));
  if (words.length <= 2) return words.join(" and ");
  return `${words.slice(0, 2).join(", ")} and ${words.length - 2} more`;
}

/** The agent's steps for one turn: one live status line while working, a quiet summary once done. */
export function AgentActivity({
  calls,
  runs,
  live,
  nameOf,
}: {
  calls: AgentToolCall[];
  runs: AgentRunInfo[];
  live: boolean;
  /** Names a sheet or object for read and look steps. */
  nameOf?: (id: string) => string | undefined;
}) {
  if (live) {
    const running = calls.find((c) => c.state === "running");
    return (
      <span className="agent-status">
        {running
          ? callLabel(running, nameOf, true)
          : calls.length
            ? "Thinking"
            : "Reading the selection"}
      </span>
    );
  }
  if (!calls.length) return null;
  const rounds = runs.reduce((n, r) => n + r.rounds, 0);
  return (
    <details className="agent-activity-done">
      <summary>
        {stepSentence([...new Set(calls.map((c) => callLabel(c, nameOf)))])}
        {calls.some((c) => c.state === "failed") && " · a step failed"}
        <ChevronRight size={13} />
      </summary>
      <ol className="agent-activity">
        {calls.map((c) => (
          <li key={c.id} data-state={c.state}>
            <span>
              {callLabel(c, nameOf)}
              {callDetail(c) &&
                !callLabel(c, nameOf).includes(callDetail(c)) && (
                  <small>{callDetail(c)}</small>
                )}
              {c.error && (
                <small className="agent-error-text">
                  {plainToolError(c.error)}
                </small>
              )}
            </span>
          </li>
        ))}
      </ol>
      {runs.length > 0 && (
        <small className="agent-usage">
          {modelLabel(runs[0])} · {rounds} model call
          {rounds === 1 ? "" : "s"} ·{" "}
          {tokens(runs.reduce((n, r) => n + r.inputTokens + r.outputTokens, 0))}{" "}
          tokens
        </small>
      )}
    </details>
  );
}

export type ChangeSummary = {
  id: string;
  verb: "Added" | "Updated" | "Moved" | "Resized" | "Rewrote" | "Removed";
  noun: string;
  label: string;
  record: TLRecord;
};
const shapeNouns: Record<string, string> = {
  sheet: "sheet",
  text: "text",
  geo: "shape",
  line: "line",
  image: "image",
  arrow: "arrow",
  draw: "sketch",
  note: "sticky note",
  scene: "3D model",
};
/** Describe a record change in the user's words. Assets and bindings are omitted. */
export function describeChange(
  id: string,
  change: DraftChange,
): ChangeSummary | null {
  const record = change.after ?? change.before;
  if (!record) return null;
  if (record.typeName !== "shape" && record.typeName !== "comment") return null;
  const noun =
    record.typeName === "comment"
      ? "comment"
      : record.type === "drafting" || record.type === "mark"
        ? record.props.kind
        : record.type === "geo" && "geo" in record.props
          ? String(record.props.geo)
          : (shapeNouns[record.type] ?? record.type);
  let verb: ChangeSummary["verb"] = "Updated";
  if (!change.before) verb = "Added";
  else if (!change.after) verb = "Removed";
  else if (change.before.typeName === "shape" && record.typeName === "shape") {
    const b = change.before,
      a = record;
    const props = (r: TLRecord) =>
      JSON.stringify(r.typeName === "shape" ? r.props : {});
    const bText = "richText" in b.props ? JSON.stringify(b.props.richText) : "";
    const aText = "richText" in a.props ? JSON.stringify(a.props.richText) : "";
    if (bText !== aText) verb = "Rewrote";
    // A 3D model's props hold the model itself, so a change there is an edit.
    else if (a.type === "scene" && "spec" in a.props && "spec" in b.props && a.props.spec !== b.props.spec)
      verb = "Updated";
    else if (props(b) !== props(a)) verb = "Resized";
    else if (b.x !== a.x || b.y !== a.y || b.parentId !== a.parentId)
      verb = "Moved";
  }
  const label = recordLabel(record);
  return {
    id,
    verb,
    noun,
    label:
      label === record.typeName ||
      label === noun ||
      (record.typeName === "shape" && label === record.type)
        ? ""
        : label,
    record,
  };
}

function host(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}
/** Web pages and images the agent collected or generated in a turn. */
export function AgentResourceCards({
  resources,
  openScene,
  showShape,
  hasShape,
}: {
  resources: AgentResource[];
  openScene?: (scene: SceneSpec) => void;
  /** Select and frame a board object the agent placed. */
  showShape?: (id: string) => void;
  hasShape?: (id: string) => boolean;
}) {
  const images = resources.filter((r) => r.src);
  const pages = resources.filter(
    (r) => !r.src && (r.kind === "web_page" || r.kind === "web_search"),
  );
  const scenes = resources.filter((r) => r.kind === "scene" && r.scene);
  if (!images.length && !pages.length && !scenes.length) return null;
  return (
    <div className="agent-resource-cards">
      {scenes.map((r) => {
        const placed = !!(r.shapeId && hasShape?.(r.shapeId));
        const scale = r.scene!.approximate
          ? "traced, approximate"
          : r.scene!.calibrated
          ? "to scale"
          : r.scene!.plan
            ? "scale assumed"
            : "in metres";
        return (
          <div key={r.id} className="agent-resource-scene">
            <button
              className="agent-resource-scene-main"
              onClick={() =>
                placed ? showShape?.(r.shapeId!) : openScene?.(r.scene!)
              }
              title={placed ? "Show it on the canvas" : "Open the 3D model"}
            >
              <SceneThumbnail scene={r.scene!} />
              <span>
                {r.title}
                <small>
                  {placed ? "3D model on the canvas" : "3D model"} · {scale}
                </small>
              </span>
            </button>
            {openScene && (
              <button
                className="agent-resource-scene-open"
                onClick={() => openScene(r.scene!)}
                title="Open full screen"
                aria-label={`Open ${r.title} full screen`}
              >
                <Box size={14} />
              </button>
            )}
          </div>
        );
      })}
      {images.length > 0 && (
        <div className="agent-resource-images">
          {images.map((r) => {
            const href = r.pageUrl ?? r.url;
            const body = (
              <>
                <img src={`${r.src}?size=256`} alt={r.title} loading="lazy" />
                <small>
                  {r.kind === "generated_image"
                    ? "Generated"
                    : r.author
                      ? `${r.author}${r.licence ? ` · ${r.licence}` : ""}`
                      : href
                        ? host(href)
                        : "Reference"}
                </small>
              </>
            );
            return href ? (
              <a
                key={r.id}
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                title={r.prompt ?? r.title}
              >
                {body}
              </a>
            ) : (
              <span key={r.id} title={r.prompt ?? r.title}>
                {body}
              </span>
            );
          })}
        </div>
      )}
      {pages.map((r) => {
        const href = r.pageUrl ?? r.url;
        if (!href) return null;
        return (
          <a
            key={r.id}
            className="agent-resource-page"
            href={href}
            target="_blank"
            rel="noreferrer noopener"
          >
            <span>
              {r.title || host(href)}
              <small>{host(href)}</small>
            </span>
            <ExternalLink size={12} />
          </a>
        );
      })}
    </div>
  );
}

const THUMB_FILL: Record<string, string> = {
  glass: "#bcd6e2",
  landscape: "#b5c8a5",
  water: "#a9c4d6",
  brick: "#c98d78",
  timber: "#d2b08a",
  highlight: "#e58a6c",
  limestone: "#e3d8c4",
  concrete: "#d3d0c9",
};
/** A static axonometric sketch of the scene: tops of each element, back to front. */
export function SceneThumbnail({ scene }: { scene: SceneSpec }) {
  const e = sceneExtent(scene);
  const cos = Math.cos(Math.PI / 6),
    sin = Math.sin(Math.PI / 6);
  const project = (x: number, y: number, z: number) => [
    (x - e.minX - (y - e.minY)) * cos,
    (x - e.minX + (y - e.minY)) * sin - z,
  ];
  const faces = scene.elements
    .flatMap((el) => {
      const kind = elementKind(el);
      const material = (el as { material?: string }).material ?? "";
      const fill =
        kind === "tree"
          ? "#c3d0b6"
          : (THUMB_FILL[material] ?? (kind === "roof" ? "#bdb8b0" : "#f5f1e8"));
      return elementOutline(el).map((o, i) => ({
        key: `${el.id}_${i}`,
        fill,
        pts: o.points.map(([x, y]) => project(x, y, o.top)),
        base: o.points.map(([x, y]) => project(x, y, o.bottom)),
        depth:
          o.points.reduce((n, [x, y]) => n + x + y, 0) / o.points.length +
          o.top * 0.01,
      }));
    })
    .sort((a, b) => a.depth - b.depth);
  const all = faces.flatMap((f) => [...f.pts, ...f.base]);
  const xs = all.map((p) => p[0]),
    ys = all.map((p) => p[1]);
  const minX = Math.min(...xs),
    minY = Math.min(...ys);
  const w = Math.max(...xs) - minX || 1,
    h = Math.max(...ys) - minY || 1;
  const pad = Math.max(w, h) * 0.06;
  return (
    <svg
      className="agent-scene-thumb"
      viewBox={`${minX - pad} ${minY - pad} ${w + pad * 2} ${h + pad * 2}`}
      aria-hidden="true"
    >
      {faces.map(({ key, fill, pts, base }) => (
        <g key={key}>
          {pts.map((p, i) => (
            <line key={i} x1={p[0]} y1={p[1]} x2={base[i][0]} y2={base[i][1]} />
          ))}
          <polygon points={pts.map((p) => p.join(",")).join(" ")} style={{ fill }} />
        </g>
      ))}
    </svg>
  );
}
